import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {
  Engine,
  type EngineOptions,
  type OperatorEvaluator,
  type RuleProperties,
} from 'json-rules-engine';

dayjs.extend(isBetween);

enum OPERATORS {
  IS = 'is',
  IS_NOT = 'is_not',
  CONTAINS_DATA = 'containsData',
  DOES_NOT_CONTAIN_DATA = 'doesNotContainData',
  IS_DEFINED = 'isDefined',
  IS_NOT_DEFINED = 'isNotDefined',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  IS_AFTER = 'isAfter',
  IS_BEFORE = 'isBefore',
  BETWEEN = 'between',
  IS_DATE_DEFINED = 'isDateDefined',
  IS_DATE_NOT_DEFINED = 'isDateNotDefined',
  ALWAYS_TRUTHY = 'alwaysTruthy',
}

export const isDefinedOperator = (factValue: string) => !(typeof factValue === 'undefined' || factValue === null);

export const isNotDefinedOperator = (factValue: string) => !isDefinedOperator(factValue);

export const containsDataOperator = (factValue: string) => {
  if (!isDefinedOperator(factValue)) return false;

  if (Array.isArray(factValue) && !factValue.length) return false;

  if (typeof factValue === 'string') return !!factValue;

  return true;
};

export const doesNotContainDataOperator = (factValue: string) => !containsDataOperator(factValue);

export const startsWithOperator = (factValue: string, jsonValue: string) => {
  if (typeof factValue !== 'string' || typeof jsonValue !== 'string') return false;

  return factValue.toLowerCase().startsWith(jsonValue.toLowerCase());
};

export const endsWithOperator = (factValue: string, jsonValue: string) => {
  if (typeof factValue !== 'string' || typeof jsonValue !== 'string') return false;

  return factValue.toLowerCase().endsWith(jsonValue.toLowerCase());
};

export const isAfterDateOperator = (factValue: string, jsonValue: string) => {
  if (!(dayjs(factValue).isValid() && dayjs(jsonValue).isValid())) return false;

  return dayjs(factValue).isAfter(jsonValue);
};

export const isBeforeDateOperator = (factValue: string, jsonValue: string) => {
  if (!(dayjs(factValue).isValid() && dayjs(jsonValue).isValid())) return false;

  return dayjs(factValue).isBefore(jsonValue);
};

export const isBetweenDateOperator = (factValue: string, jsonValue: [string, string]) => {
  if (!(dayjs(factValue).isValid() && jsonValue.every((value) => dayjs(value).isValid()))) return false;

  return dayjs(factValue).isBetween(jsonValue[0], jsonValue[1]);
};

export const isDateDefinedOperator = (factValue: string) => dayjs(factValue).isValid();

export const isDateNotDefinedOperator = (factValue: string) => !dayjs(factValue).isValid();

export const alwaysTruthyOperator = () => true;

export const isOperator = (factValue: any, jsonValue: any) => {
  if (Array.isArray(jsonValue)) {
    return jsonValue.includes(factValue);
  }

  return factValue === jsonValue;
};

export const isNotOperator = (factValue: any, jsonValue: any) => !isOperator(factValue, jsonValue);

const OPERATORS_CONFIG = {
  [OPERATORS.IS]: isOperator,
  [OPERATORS.IS_NOT]: isNotOperator,
  [OPERATORS.IS_DEFINED]: isDefinedOperator,
  [OPERATORS.IS_NOT_DEFINED]: isNotDefinedOperator,
  [OPERATORS.CONTAINS_DATA]: containsDataOperator,
  [OPERATORS.DOES_NOT_CONTAIN_DATA]: doesNotContainDataOperator,
  [OPERATORS.STARTS_WITH]: startsWithOperator,
  [OPERATORS.ENDS_WITH]: endsWithOperator,
  [OPERATORS.IS_AFTER]: isAfterDateOperator,
  [OPERATORS.IS_BEFORE]: isBeforeDateOperator,
  [OPERATORS.BETWEEN]: isBetweenDateOperator,
  [OPERATORS.IS_DATE_DEFINED]: isDateDefinedOperator,
  [OPERATORS.ALWAYS_TRUTHY]: alwaysTruthyOperator,
};

export class RulesEngine extends Engine {
  constructor(rules?: Array<RuleProperties>, options?: EngineOptions) {
    super(rules, options);
    Object.entries(OPERATORS_CONFIG).forEach(
      ([operator, callbackFunction]) => this.addOperator(operator, callbackFunction as OperatorEvaluator<string, any>),
    );
  }
}
